import { useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { usePostMessage, useReceiveMessage } from '../../../context/messageContext';
import { useTestContext } from '../../../context/testContext';
import { PatientResponseSidebarVF2, VisualFieldMatrixVF2 } from '../../../components';
import { visualFieldTwoDots } from '../../../helpers/data';
import { useFormattedDots } from '../../../helpers/visual-field-two-hooks/useFormattedDots';
import { useVisualFieldData } from '../../../helpers/reducers';
import { useCalibration } from '../../../context/calibrationContext';
import { breakpoints } from '../../../ui';
import { StepBody, Divider, ConfidenceMeter, DistanceReadout, Note } from '../testScreenKit';
import VisualFieldControls from '../../../components/VisualField/VisualFieldControls';

const Top = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 24px 40px;
  align-items: center;
  justify-items: start;
  @media (max-width: ${breakpoints.phone - 1}px) {
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
  }
`;

// Step 3 - Visual Field test (Figma NEW session/12 left, landscape 21 right).
const VisualFieldTest = ({ eyeTested, updateStep }) => {
  const [testIsRunning, setTestIsRunning] = useState(false);
  const [rawDotDataset, setRawDotDataset] = useState([]);
  const { testData, updateTestData } = useTestContext();
  const { testProgressData, updateTestProgressData, batchMultipleUpdates } = useVisualFieldData();
  const postMessage = usePostMessage();
  const receivedMessage = useReceiveMessage();
  const getDotData = useFormattedDots();
  const {
    calibrationData: { circleDimensionsPx, blindSpotVanishingXDistancePx, distanceFromScreen },
  } = useCalibration();

  // const getTestProgress = (type) => {
  //   const { seenDots, notSeenDots, totalBlindSpotsShown } = testProgressData;
  //   const blindSpotCount = rawDotDataset.filter((dot) => dot.blindspot && !dot.disabled).length;
  //   const processedDots = seenDots.length + notSeenDots.length + (totalBlindSpotsShown === 5 && blindSpotCount);
  //   const allDotsCount = rawDotDataset.filter((dot) => !dot.disabled).length;

  //   const label = `${processedDots}/${allDotsCount}`;
  //   const progressPercentage = Math.round((processedDots / allDotsCount) * 100);
  //   return type === 'label' ? label : progressPercentage;
  // };

  useEffect(() => {
    updateTestData({ testInProgress: true });
  }, [updateTestData]);

  useEffect(() => {
    if (receivedMessage.type === 'useVisualFieldReducer') {
      updateTestProgressData({ type: receivedMessage.action, payload: receivedMessage.payload });
    }
  }, [receivedMessage, updateTestProgressData]);

  useEffect(() => {
    //we should handle this differently - have the setup stage communicate with a shared reducers via websocket messages for calibration and settings, possibly separate to active test data.
    postMessage({
      type: 'calibrationDataFromOptician',
      content: {
        blindSpotVanishingXDistancePx: blindSpotVanishingXDistancePx,
        circleDimensionsPx: circleDimensionsPx,
        contrastType: testData?.[eyeTested]?.['contrastType'],
        targetDuration: testData?.[eyeTested]?.['targetDuration'],
        fixationType: testData?.[eyeTested]?.['fixationType'],
      },
    });
  }, [testData, eyeTested, blindSpotVanishingXDistancePx, circleDimensionsPx, postMessage]);

  const initialiseDotData = useCallback(() => {
    const { localDots } = getDotData(visualFieldTwoDots, eyeTested);
    setRawDotDataset(localDots);
    const dotsInUse = localDots.filter((dot) => !dot.disabled);
    batchMultipleUpdates({ setOutOfBoundariesDots: localDots.filter((dot) => dot.outOfBoundaries), setDotsForDisplay: dotsInUse });
  }, [getDotData, eyeTested, batchMultipleUpdates]);

  useEffect(() => {
    initialiseDotData();
  }, [initialiseDotData]);

  const confidence = testProgressData?.testConfidence === 0 ? '0' : testProgressData?.testConfidence || 100;

  return (
    <StepBody gap={20} data-testid='vf-test'>
      <Top>
        <VisualFieldMatrixVF2 testInProgress={testIsRunning} testProgressData={testProgressData} eyeTested={eyeTested} />
        <PatientResponseSidebarVF2 testProgressData={testProgressData} />
      </Top>
      <ConfidenceMeter percentage={confidence} />
      {testProgressData?.testConfidence === 0 && <Note tone='error'>Test ended due to low confidence.</Note>}
      <Divider />
      <DistanceReadout distance={distanceFromScreen} positioned={null} />
      <VisualFieldControls
        updateStep={updateStep}
        eyeTested={eyeTested}
        testProgressData={testProgressData}
        testIsRunning={testIsRunning}
        setTestIsRunning={setTestIsRunning}
        initialiseDotData={initialiseDotData}
      />
    </StepBody>
  );
};

export default VisualFieldTest;
